import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { Container, Row, Col, Modal, Button } from 'react-bootstrap';
import {
    useGetSpotlight1Query,
    useGetSpotlight2Query,
    useGetSpotlight3Query,
    useGetSpotlight4Query,
    useGetFeaturedQuery,
    useGetNewArrivalsQuery,
    useGetActiveSalesQuery,
    useGetTopChoicesQuery,
    useGetMugsQuery,
    useGetArcadeQuery,
} from '../slices/productsApiSlice';
import { Link, useNavigate } from 'react-router-dom';
import HeaderNav from '../components/HeaderNav';
import Loader from '../components/Loader';
import Message from '../components/Message';
import StayTop from '../components/StayTop';
import Products from '../components/Products';
import GiftFinder from '../components/GiftFinder';
import AnimatedProductCard from '../components/AnimatedProductCard';
import StickyNavFix from '../components/StickyNavFix';
import WelcomeOfferBanner from '../components/WelcomeOfferBanner';
import DynamicCarousel from '../components/DynamicCarousel';
import SpotlightProduct from '../components/SpotlightProduct';
import CollectionCarousel from '../components/CollectionCarousel';
import RDWidget from '../components/RDWidget';
import PaymentMethodsBand from '../components/PaymentMethodsBand';
import '../assets/styles/landing.css';

const LandingPage = () => {
    const navigate = useNavigate();

    // ========================================
    // ADD LANDING PAGE CLASS TO BODY
    // ========================================
    useEffect(() => {
        document.body.classList.add('landing-page');
        return () => {
            document.body.classList.remove('landing-page');
        };
    }, []);

    // ========================================
    // API DATA QUERIES - CLEAN & SIMPLE
    // ========================================
    const { data: spotlight1 } = useGetSpotlight1Query();
    const { data: spotlight2 } = useGetSpotlight2Query();
    const { data: spotlight3 } = useGetSpotlight3Query();
    const { data: spotlight4 } = useGetSpotlight4Query();
    const { data: featuredProducts, isLoading: loadingFeaturedProducts, error: errorFeaturedProducts } = useGetFeaturedQuery();
    const { data: newArrivals, isLoading: loadingNewArrivals } = useGetNewArrivalsQuery();
    const { data: activeSales, isLoading: loadingActiveSales } = useGetActiveSalesQuery();
    const { data: topChoices, isLoading: loadingTopChoices } = useGetTopChoicesQuery();
    const { data: mugs, isLoading: loadingMugs } = useGetMugsQuery();
    const { data: arcade, isLoading: loadingArcade } = useGetArcadeQuery();

    // ========================================
    // STATE MANAGEMENT
    // ========================================
    const [showCookieModal, setShowCookieModal] = useState(true);
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ========================================
    // HELPER FUNCTION TO TRUNCATE DESCRIPTION
    // ========================================
    const truncateDescription = (description, maxLength = 200) => {
        if (!description) return '';
        const cleanDescription = description.replace(/\\n/g, '\n');
        if (cleanDescription.length <= maxLength) return cleanDescription;
        return cleanDescription.substring(0, maxLength) + '...';
    };

    // ========================================
    // COOKIE MODAL HANDLERS
    // ========================================
    const handleAcceptCookies = () => {
        localStorage.setItem('cookiesAccepted', 'true');
        setShowCookieModal(false);
    };

    const handleRejectCookies = () => {
        localStorage.setItem('cookiesAccepted', 'false');
        setShowCookieModal(false);
    };

    useEffect(() => {
        const cookiesAccepted = localStorage.getItem('cookiesAccepted');
        if (cookiesAccepted) {
            setShowCookieModal(false);
        }
    }, []);

    // ========================================
    // NEWSLETTER FORM HANDLER - UPDATED WITH MAILCHIMP
    // ========================================
    const handleNewsletterSubmit = async (e) => {
        e.preventDefault();

        // Prevent double submissions
        if (isSubmitting) return;

        setIsSubmitting(true);

        try {
            const { data } = await axios.post('/api/newsletter/subscribe', {
                email,
                source: 'landing_page'
            });

            // Success! Show fun retro message
            toast.success(data.message, {
                position: 'top-center',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });

            // Clear the email input
            setEmail('');

        } catch (error) {
            // Show error message
            const message = error.response?.data?.message || 'Oops! Something went wrong. Please try again.';

            toast.error(message, {
                position: 'top-center',
                autoClose: 5000,
            });

        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <StickyNavFix />
            <StayTop />
            <HeaderNav />

            <RDWidget onSend={async (message, history) => {
                const excludeIds = history
                    .filter((m) => m.role === 'rd' && m.products)
                    .flatMap((m) => m.products.map((p) => p.id));

                const res = await fetch('/api/agent/chat', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ message, history, excludeIds }),
                });
                return res.json();
            }} />

            {/* Spotlight & Mobile Styles */}
            <style>{`
                .spotlight-header-badge {
                    font-size: 4rem;
                    margin-bottom: 0;
                    filter: drop-shadow(0 4px 10px rgba(0,0,0,0.1));
                }

                .spotlight-header-badge svg {
                    width: 1em;
                    height: 1em;
                    --fa-primary-color: #FD7014;
                    --fa-secondary-color: #3E6D9C;
                    --fa-secondary-opacity: 0.6;
                }

                .spotlight-section-title {
                    font-size: 2.5rem;
                    font-weight: 800;
                    background: linear-gradient(135deg, #3E6D9C 0%, #FD7014 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    margin-bottom: 0.5rem;
                    margin-top: 2.5rem;
                    text-transform: uppercase;
                    letter-spacing: 2px;
                }

                .spotlight-section-subtitle {
                    font-size: 1.2rem;
                    color: #666;
                    font-weight: 300;
                    font-style: italic;
                    margin-bottom: 2rem;
                }

                .spotlight-section-header::after {
                    content: '';
                    width: 100px;
                    height: 4px;
                    background: linear-gradient(90deg, #3E6D9C, #FD7014);
                    display: block;
                    margin: 1.5rem auto 0;
                    border-radius: 2px;
                    margin-bottom: -1rem;
                }

                /* Tablet and below */
                @media (max-width: 768px) {
                    .spotlight-section-header {
                        flex-direction: column !important;
                        gap: 1rem !important;
                        text-align: center;
                        margin-top: 0.5rem;
                        margin-bottom: 1rem;
                    }
                    
                    .spotlight-section-header > div {
                        text-align: center;
                    }

                    .spotlight-header-badge {
                        font-size: 3.5rem;
                    }

                    .spotlight-section-title {
                        font-size: 2rem;
                        letter-spacing: 1.5px;
                    }

                    .spotlight-section-subtitle {
                        font-size: 1.1rem;
                    }
                }

                /* Small phones */
                @media (max-width: 430px) {
                    .spotlight-header-badge {
                        font-size: 2.5rem;
                    }

                    .spotlight-section-title {
                        font-size: 1.5rem;
                        letter-spacing: 0.5px;
                    }

                    .spotlight-section-subtitle {
                        font-size: 0.9rem;
                    }
                }

                /* Trust Badges - Mobile Responsive */
                @media (max-width: 768px) {
                    .trust-badges-section-compact {
                        padding: 1.5rem 0 !important;
                    }

                    .trust-badges-grid-compact {
                        grid-template-columns: repeat(4, 1fr) !important;
                        gap: 0.5rem !important;
                    }

                    .trust-badge-item-compact {
                        padding: 0.5rem 0.25rem !important;
                    }

                    .trust-badge-icon {
                        font-size: 1.2rem !important;
                        margin-bottom: 0.25rem !important;
                    }

                    .trust-badge-title {
                        font-size: 0.7rem !important;
                        margin-bottom: 0.1rem !important;
                        line-height: 1.2;
                    }

                    .trust-badge-description {
                        font-size: 0.6rem !important;
                        line-height: 1.2;
                    }
                }

                @media (max-width: 430px) {
                    .trust-badges-section-compact {
                        padding: 1rem 0 !important;
                    }

                    .trust-badges-grid-compact {
                        gap: 0.35rem !important;
                    }

                    .trust-badge-item-compact {
                        padding: 0.4rem 0.2rem !important;
                    }

                    .trust-badge-icon {
                        font-size: 1rem !important;
                        margin-bottom: 0.2rem !important;
                    }

                    .trust-badge-title {
                        font-size: 0.65rem !important;
                        font-weight: 600 !important;
                    }

                    .trust-badge-description {
                        font-size: 0.55rem !important;
                    }
                }

                /* Customer Favorites - Mobile Scroll */
                .customer-favorites-mobile {
                    display: none;
                }

                .customer-favorites-desktop {
                    display: flex;
                }

                @media (max-width: 768px) {
                    .customer-favorites-desktop {
                        display: none;
                    }

                    .customer-favorites-mobile {
                        display: block;
                        margin: 0 -1rem;
                        overflow: hidden;
                    }

                    .customer-favorites-scroll {
                        display: flex;
                        overflow-x: auto;
                        scroll-snap-type: x mandatory;
                        -webkit-overflow-scrolling: touch;
                        scrollbar-width: none;
                        padding: 0 1rem;
                        gap: 1rem;
                    }

                    .customer-favorites-scroll::-webkit-scrollbar {
                        display: none;
                    }

                    .customer-favorites-item {
                        flex: 0 0 85%;
                        max-width: 85%;
                        scroll-snap-align: center;
                    }
                }

                /* Testimonials - Mobile Scroll */
                .testimonials-mobile {
                    display: none;
                }

                .testimonials-desktop {
                    display: grid;
                }

                @media (max-width: 768px) {
                    .testimonials-desktop {
                        display: none;
                    }

                    .testimonials-mobile {
                        display: block;
                        margin: 0 -1rem;
                        overflow: hidden;
                    }

                    .testimonials-scroll {
                        display: flex;
                        overflow-x: auto;
                        scroll-snap-type: x mandatory;
                        -webkit-overflow-scrolling: touch;
                        scrollbar-width: none;
                        padding: 0 1rem;
                        gap: 1rem;
                    }

                    .testimonials-scroll::-webkit-scrollbar {
                        display: none;
                    }

                    .testimonial-item {
                        flex: 0 0 85%;
                        max-width: 85%;
                        scroll-snap-align: center;
                    }
                }
            `}</style>

            {/* ========================================
                HERO CAROUSEL
            ======================================== */}
            <DynamicCarousel />

            {/* ========================================
                PROMO BANNER
            ======================================== */}
            <WelcomeOfferBanner />

            {/* ========================================
                TRUST BADGES
            ======================================== */}
            <section className="trust-badges-section-compact">
                <Container>
                    <div className="trust-badges-grid-compact">
                        <div className="trust-badge-item-compact">
                            <div className="trust-badge-icon">🚚</div>
                            <div className="trust-badge-title">Free Shipping</div>
                            <div className="trust-badge-description">On orders over £50</div>
                        </div>
                        <div className="trust-badge-item-compact">
                            <div className="trust-badge-icon">🔒</div>
                            <div className="trust-badge-title">Secure Checkout</div>
                            <div className="trust-badge-description">Payments secured by Stripe</div>
                        </div>
                        <div className="trust-badge-item-compact">
                            <div className="trust-badge-icon">↩️</div>
                            <div className="trust-badge-title">14-Day Returns</div>
                            <div className="trust-badge-description">Hassle-free returns</div>
                        </div>
                        <div className="trust-badge-item-compact">
                            <div className="trust-badge-icon">💳</div>
                            <div className="trust-badge-title">Pay Later</div>
                            <div className="trust-badge-description">Interest-free with Klarna &amp; Clearpay</div>
                        </div>
                    </div>
                    <div style={{ marginTop: '1.5rem' }}>
                        <PaymentMethodsBand />
                    </div>
                </Container>
            </section>

            {/* ========================================
                GIFT FINDER
            ======================================== */}
            <section className="product-spotlight">
                <GiftFinder />
            </section>

            {/* ========================================
                COLLECTION CAROUSELS
            ======================================== */}
            <Container fluid>
                <CollectionCarousel
                    title="New Arrivals"
                    subtitle="Fresh into the collection"
                    viewAllLink="/search?sort=newest"
                    products={newArrivals}
                    isLoading={loadingNewArrivals}
                />

                {activeSales && activeSales.length > 0 && (
                    <CollectionCarousel
                        title="On Sale Now"
                        subtitle="Don't miss these deals"
                        viewAllLink="/search?onSale=true"
                        products={activeSales}
                        isLoading={loadingActiveSales}
                    />
                )}

                <CollectionCarousel
                    title="Top Choices"
                    subtitle="Handpicked by us, loved by you"
                    viewAllLink="/search"
                    products={topChoices}
                    isLoading={loadingTopChoices}
                />
            </Container>

            <Container fluid>
                {/* ========================================
                    SPOTLIGHT PRODUCTS
                ======================================== */}
                {(spotlight1 || spotlight2 || spotlight3 || spotlight4) && (
                    <section className="product-spotlight rd-featured">
                        <Container>
                            <div className="spotlight-section-header">
                                <h2 className="spotlight-section-title">Featured This Week</h2>
                                <p className="spotlight-section-subtitle">Handpicked highlights from the collection</p>
                            </div>
                        </Container>
                        {[
                            { product: spotlight1, n: 1, reverse: false },
                            { product: spotlight4, n: 4, reverse: true },
                            { product: spotlight2, n: 2, reverse: false },
                            { product: spotlight3, n: 3, reverse: true },
                        ].filter((s) => s.product).map((s) => (
                            <SpotlightProduct
                                key={s.n}
                                product={s.product}
                                spotlightNumber={s.n}
                                reverseLayout={s.reverse}
                                hideHeader
                                truncateDescription={truncateDescription}
                            />
                        ))}
                    </section>
                )}

                {/* ========================================
                    POST-SPOTLIGHT COLLECTION CAROUSELS
                ======================================== */}
                <CollectionCarousel
                    title="Mugs Collection"
                    subtitle="Start your morning the retro way"
                    viewAllLink="/search/Mugs"
                    products={mugs}
                    isLoading={loadingMugs}
                />

                <CollectionCarousel
                    title="Arcade & Gaming"
                    subtitle="Level up your collection"
                    viewAllLink="/search/Collectibles"
                    products={arcade}
                    isLoading={loadingArcade}
                />

                {/* ========================================
                    CUSTOMER TESTIMONIALS
                ======================================== */}
                <section className="product-spotlight">
                    <Container>
                        <div className="spotlight-section-header">
                            <h2 className="spotlight-section-title">What Our Customers Say</h2>
                            <p className="spotlight-section-subtitle">Real reviews from real retro lovers</p>
                        </div>

                        {/* Desktop Grid */}
                        <div className="testimonials-grid-improved testimonials-desktop">
                            <div className="testimonial-card-improved">
                                <div className="testimonial-rating">★★★★★</div>
                                <p className="testimonial-text">
                                    "Ordered my Retrodayz cap for my Holidays. It arrived promptly and well packaged.
                                    I ordered the green and it didn't disappoint! Very good quality and the green was lovely and vibrant"
                                </p>
                                <div className="testimonial-author">
                                    <div className="testimonial-avatar">CS</div>
                                    <div className="testimonial-author-info">
                                        <div className="testimonial-author-name">Cathy Smith</div>
                                        <div className="testimonial-author-location">Runcorn, UK</div>
                                    </div>
                                </div>
                            </div>

                            <div className="testimonial-card-improved">
                                <div className="testimonial-rating">★★★★★</div>
                                <p className="testimonial-text">
                                    "Got the Pac-Man lamp for my gaming room and it's become the centerpiece!
                                    Excellent build quality and the nostalgic glow is perfect for late-night gaming sessions."
                                </p>
                                <div className="testimonial-author">
                                    <div className="testimonial-avatar">SE</div>
                                    <div className="testimonial-author-info">
                                        <div className="testimonial-author-name">Sarah Edwards</div>
                                        <div className="testimonial-author-location">Bristol, UK</div>
                                    </div>
                                </div>
                            </div>

                            <div className="testimonial-card-improved">
                                <div className="testimonial-rating">★★★★★</div>
                                <p className="testimonial-text">
                                    "I wanted a Spider-Man mug. I ordered this Spider-Man mug. Now I drink my brew from a Spider-Man mug. Mission accomplished!"
                                </p>
                                <div className="testimonial-author">
                                    <div className="testimonial-avatar">TP</div>
                                    <div className="testimonial-author-info">
                                        <div className="testimonial-author-name">Alan Turner</div>
                                        <div className="testimonial-author-location">Widnes, UK</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Mobile Scroll */}
                        <div className="testimonials-mobile">
                            <div className="testimonials-scroll">
                                {[
                                    { avatar: "CS", name: "Cathy Smith", location: "Runcorn, UK", text: "Ordered my Retrodayz cap for my Holidays. It arrived promptly and well packaged. I ordered the green and it didn't disappoint! Very good quality and the green was lovely and vibrant" },
                                    { avatar: "SE", name: "Sarah Edwards", location: "Bristol, UK", text: "Got the Pac-Man lamp for my gaming room and it's become the centerpiece! Excellent build quality and the nostalgic glow is perfect for late-night gaming sessions." },
                                    { avatar: "TP", name: "Alan Tuner", location: "Widnes, UK", text: "So I wanted a brew in a Spiderman cup so I ordered a Spiderman cup.\nThis was the Spiderman cup that I ordered because I wanted it.\nI got it and used it to drink a brew in.\nHope that explains it for you Phillip Williams III" }
                                ].map((testimonial, index) => (
                                    <div key={index} className="testimonial-item">
                                        <div className="testimonial-card-improved">
                                            <div className="testimonial-rating">★★★★★</div>
                                            <p className="testimonial-text">{testimonial.text}</p>
                                            <div className="testimonial-author">
                                                <div className="testimonial-avatar">{testimonial.avatar}</div>
                                                <div className="testimonial-author-info">
                                                    <div className="testimonial-author-name">{testimonial.name}</div>
                                                    <div className="testimonial-author-location">{testimonial.location}</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Container>
                </section>

                {/* ========================================
                    CUSTOMER FAVORITES
                ======================================== */}
                <section className="product-spotlight">
                    <Container>
                        <div className="spotlight-section-header">
                            <h2 className="spotlight-section-title">Customer Favorites</h2>
                            <p className="spotlight-section-subtitle">Most Loved by Our Retro Community</p>
                        </div>

                        {loadingFeaturedProducts ? (
                            <Loader />
                        ) : errorFeaturedProducts ? (
                            <Message variant="danger">
                                {errorFeaturedProducts.message || errorFeaturedProducts.data?.message || "An error occurred"}
                            </Message>
                        ) : (
                            <>
                                {/* Desktop Grid */}
                                <Row className="justify-content-center customer-favorites-desktop">
                                    {featuredProducts?.slice(0, 4).map((product, index) => (
                                        <Col key={product._id} xs={12} sm={6} md={6} lg={3} xl={3}>
                                            <AnimatedProductCard index={index}>
                                                <Products product={product} showDiscount={product.isClearance} />
                                            </AnimatedProductCard>
                                        </Col>
                                    ))}
                                </Row>

                                {/* Mobile Scroll */}
                                <div className="customer-favorites-mobile">
                                    <div className="customer-favorites-scroll">
                                        {featuredProducts?.slice(0, 4).map((product) => (
                                            <div key={product._id} className="customer-favorites-item">
                                                <Products product={product} showDiscount={product.isClearance} />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </Container>
                </section>

                {/* ========================================
                    NEWSLETTER - UPDATED WITH PROPER UX
                ======================================== */}
                <section className="social-proof-newsletter-combined">
                    <Container>
                        <div className="newsletter-section-inline">
                            <h2 className="newsletter-title">Stay In The Loop! 📧</h2>
                            <p className="newsletter-subtitle">
                                Get exclusive deals, new arrivals, and retro news delivered to your inbox
                            </p>

                            <form onSubmit={handleNewsletterSubmit} className="newsletter-form">
                                <input
                                    type="email"
                                    placeholder="Enter your email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    disabled={isSubmitting}
                                    className="newsletter-input"
                                />
                                <button
                                    type="submit"
                                    className="newsletter-btn"
                                    disabled={isSubmitting}
                                    style={{
                                        backgroundColor: isSubmitting ? '#ccc' : '#FD7014',
                                        borderColor: isSubmitting ? '#ccc' : '#FD7014',
                                        color: '#FFFFFF',
                                        cursor: isSubmitting ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {isSubmitting ? 'Subscribing...' : 'Subscribe Now'}
                                </button>
                            </form>
                        </div>
                    </Container>
                </section>
            </Container>

            {/* ========================================
                COOKIE CONSENT MODAL
            ======================================== */}
            <Modal show={showCookieModal} onHide={handleAcceptCookies}>
                <Modal.Header closeButton>
                    <Modal.Title>Cookie Policy</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    We use cookies to improve your experience on our site and to show you personalized advertising.
                    By accepting, you agree to our use of cookies as described in our <Link to="/cookie">Cookie Policy</Link>.
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        style={{ backgroundColor: '#3E6D9C', borderColor: '#FD7014' }}
                        onClick={handleAcceptCookies}
                    >
                        Accept
                    </Button>
                    <Button
                        style={{ backgroundColor: '#FD7014', borderColor: '#3E6D9C' }}
                        onClick={handleRejectCookies}
                    >
                        Reject
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default LandingPage;